import { useState, useEffect, useMemo } from 'react';
import { 
  subDays, eachDayOfInterval, format, differenceInDays, addDays 
} from 'date-fns';

// Deterministic pseudo-random generator based on a seed string
function mulberry32(a) {
    return function() {
      var t = a += 0x6D2B79F5;
      t = Math.imul(t ^ t >>> 15, t | 1);
      t ^= t + Math.imul(t ^ t >>> 7, t | 61);
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    }
}

function getDaySeed(dateStr) {
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    hash = Math.imul(31, hash) + dateStr.charCodeAt(i) | 0;
  }
  return hash;
}

export const useRealTimeData = (dateRange) => {
  const data = useMemo(() => {
    const end = dateRange.end;
    const start = dateRange.start;
    const dayCount = differenceInDays(end, start) || 1;
    
    const BASE_CALLS_PER_DAY = 230000;
    
    let totalCalls = 0;
    let totalSuccessful = 0;
    let totalUnsuccessful = 0;
    let totalOutbound = 0;
    let totalInbound = 0;
    let totalPositive = 0;
    let totalNeutral = 0;
    let totalNegative = 0;
    let totalDisconnUser = 0;
    let totalDisconnVoicemail = 0;
    let totalDisconnSysErr = 0;
    let totalDurationSeconds = 0;
    let totalLatencyMs = 0;
    
    const timeline = [];
    
    const daysArray = eachDayOfInterval({ start, end });
    
    daysArray.forEach((currentDay) => {
      const dateStr = format(currentDay, 'MMM dd, yyyy');
      const random = mulberry32(getDaySeed(dateStr));
      
      const dailyVariation = 0.85 + (random() * 0.3);
      let dailyCalls = Math.floor(BASE_CALLS_PER_DAY * dailyVariation);
      totalCalls += dailyCalls;
      
      const avgConcurrency = Math.floor((dailyCalls / 1440) * 2.5);
      const peakConcurrency = Math.floor(avgConcurrency * 3.5);
      
      // Success Rate: Base 94%, +/- 3%
      let dailySuccessRate = 0.94 + (random() * 0.06 - 0.03);
      let dSuccess = Math.floor(dailyCalls * dailySuccessRate);
      let dUnsuccess = dailyCalls - dSuccess;
      totalSuccessful += dSuccess;
      totalUnsuccessful += dUnsuccess;

      // Inbound/Outbound: Base 88% Outbound, +/- 3%
      let dailyOutboundRate = 0.88 + (random() * 0.06 - 0.03);
      let dOutbound = Math.floor(dailyCalls * dailyOutboundRate);
      let dInbound = dailyCalls - dOutbound;
      totalOutbound += dOutbound;
      totalInbound += dInbound;

      // Picked Up Rate (Answered): Base 55%, +/- 5%
      let dailyPickedUpRate = 55 + (random() * 10 - 5);

      // Lead Booking Rate: Base 18%, +/- 4%
      let dailyLeadBookingRate = 18 + (random() * 8 - 4);

      // Transfer Rate: Base 15%, +/- 3%
      let dailyTransferRate = 15 + (random() * 6 - 3);

      // Voicemail Rate: Base 42%, +/- 4%
      let dailyVoicemailRate = 42 + (random() * 8 - 4);
      
      // Contact Ratio: Base 54%, +/- 5.5% (approx 48-59%)
      let dailyContactRatio = 54 + (random() * 11 - 5.5);

      // Sentiment
      let dailyPosRate = 0.55 + (random() * 0.06 - 0.03);
      let dailyNegRate = 0.15 + (random() * 0.06 - 0.03);
      let dailyNeuRate = 1 - dailyPosRate - dailyNegRate;
      totalPositive += Math.floor(dailyCalls * dailyPosRate);
      totalNegative += Math.floor(dailyCalls * dailyNegRate);
      totalNeutral += Math.floor(dailyCalls * dailyNeuRate);

      // Disconnection
      let dailyVoicemailDisconnRate = dailyVoicemailRate / 100;
      let dailySysErrRate = 0.12 + (random() * 0.04 - 0.02);
      let dailyUserDisconnRate = 1 - dailyVoicemailDisconnRate - dailySysErrRate;
      totalDisconnVoicemail += Math.floor(dailyCalls * dailyVoicemailDisconnRate);
      totalDisconnSysErr += Math.floor(dailyCalls * dailySysErrRate);
      totalDisconnUser += Math.floor(dailyCalls * dailyUserDisconnRate);

      let dailyDuration = Math.floor(120 + (random() * 30));
      let dailyLatency = Math.floor(350 + (random() * 80));
      totalDurationSeconds += dailyDuration * dailyCalls;
      totalLatencyMs += dailyLatency * dailyCalls;
      
      // Speed to Lead: Base 2.4s, +/- 0.8s
      let dailySpeedToLead = 2.4 + (random() * 1.6 - 0.8);
      
      // Show Up Rate Per Setter: Base 40%, +/- 3%
      let dailyShowUpRate = 40 + (random() * 6 - 3);

      // Average Dial Per Contact: Base 17, +/- 3 (range 14-20)
      let dailyAvgDialPerContact = 17 + (random() * 6 - 3);
      
      // Curation Rate: Base 23%, +/- 3% (scaled down proportionally with Contact Ratio)
      let dailyCurationRate = 23 + (random() * 6 - 3);

      // Average Value Per Contact Made
      // Math: (Lead Booking Rate / 100) * (Show Up Rate / 100) * 33% Close * $2500 Ticket
      let dailyAvgValuePerContact = (dailyLeadBookingRate / 100) * (dailyShowUpRate / 100) * 0.33 * 2500;

      // Average Number Rotations Per Contact: Base 4.5, +/- 0.5 (range 4-5)
      let dailyAvgNumberRotations = 4.5 + (random() * 1.0 - 0.5);

      // Bad Number Rate: Base 1.2%, +/- 0.4%
      let dailyBadNumberRate = 1.2 + (random() * 0.8 - 0.4);

      timeline.push({
        date: dateStr,
        value: dailyCalls,
        p1: peakConcurrency,
        p2: avgConcurrency,
        pickedUp: dailyPickedUpRate,
        success: dailySuccessRate * 100,
        leadBooking: dailyLeadBookingRate,
        transfer: dailyTransferRate,
        voicemail: dailyVoicemailRate,
        contactRatio: dailyContactRatio,
        speedToLead: dailySpeedToLead,
        showUpRate: dailyShowUpRate,
        avgDialPerContact: dailyAvgDialPerContact,
        curationRate: dailyCurationRate,
        avgValuePerContact: dailyAvgValuePerContact,
        avgNumberRotations: dailyAvgNumberRotations,
        badNumberRate: dailyBadNumberRate,
        duration: dailyDuration,
        latency: dailyLatency,
        'User disconnected': Math.floor(dailyCalls * dailyUserDisconnRate),
        'Voicemail': Math.floor(dailyCalls * dailyVoicemailDisconnRate),
        'System error': Math.floor(dailyCalls * dailySysErrRate),
        'Successful': dSuccess,
        'Unsuccessful': dUnsuccess
      });
    });

    let avgDurationSec = Math.floor(totalDurationSeconds / totalCalls);
    let avgDurationStr = `${Math.floor(avgDurationSec / 60)}m ${avgDurationSec % 60}s`;
    let avgLatencyMs = Math.floor(totalLatencyMs / totalCalls);

    return {
      summary: {
        callCounts: totalCalls,
        duration: avgDurationStr,
        latency: `${avgLatencyMs}ms`
      },
      callCountsTimeline: timeline,
      concurrencyTimeline: timeline,
      callSuccessful: [
        { name: 'Successful', value: totalSuccessful, color: '#3B82F6' },
        { name: 'Unsuccessful', value: totalUnsuccessful, color: '#8B5CF6' }
      ],
      disconnectionReason: [
        { name: 'User disconnected', value: totalDisconnUser, color: '#3B82F6' },
        { name: 'Voicemail', value: totalDisconnVoicemail, color: '#8B5CF6' },
        { name: 'System error', value: totalDisconnSysErr, color: '#F59E0B' }
      ],
      userSentiment: [
        { name: 'Positive', value: totalPositive, color: '#3B82F6' },
        { name: 'Neutral', value: totalNeutral, color: '#8B5CF6' },
        { name: 'Negative', value: totalNegative, color: '#F59E0B' }
      ],
      inboundOutbound: [
        { name: 'Outbound', value: totalOutbound, color: '#3B82F6' },
        { name: 'Inbound', value: totalInbound, color: '#2DD4BF' }
      ],
      pickedUpRate: timeline.map(d => ({ date: d.date, value: Math.floor(d.pickedUp) })),
      successRate: timeline.map(d => ({ date: d.date, value: Math.floor(d.success) })),
      leadBookingRate: timeline.map(d => ({ date: d.date, value: Math.floor(d.leadBooking) })),
      transferRate: timeline.map(d => ({ date: d.date, value: Math.floor(d.transfer) })),
      voicemailRate: timeline.map(d => ({ date: d.date, value: Math.floor(d.voicemail) })),
      contactRatio: timeline.map(d => ({ date: d.date, value: Math.floor(d.contactRatio) })),
      speedToLead: timeline.map(d => ({ date: d.date, value: Number(d.speedToLead.toFixed(1)) })),
      showUpRate: timeline.map(d => ({ date: d.date, value: Math.floor(d.showUpRate) })),
      avgDialPerContact: timeline.map(d => ({ date: d.date, value: Math.floor(d.avgDialPerContact) })),
      curationRate: timeline.map(d => ({ date: d.date, value: Math.floor(d.curationRate) })),
      avgValuePerContact: timeline.map(d => ({ date: d.date, value: Number(d.avgValuePerContact.toFixed(2)) })),
      avgNumberRotations: timeline.map(d => ({ date: d.date, value: Number(d.avgNumberRotations.toFixed(1)) })),
      badNumberRate: timeline.map(d => ({ date: d.date, value: Number(d.badNumberRate.toFixed(1)) })),
      avgDuration: timeline.map(d => ({ date: d.date, value: d.duration })),
      avgLatency: timeline.map(d => ({ date: d.date, value: d.latency })),
    };
  }, [dateRange.start, dateRange.end]);

  return data;
};
